// src/components/EstimationSemblables.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";

function isTeamRole(role) {
  return ["admin", "team"].includes(role);
}

function toNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function formatEuro(value, locale) {
  const n = toNumber(value);
  if (n === null) return "—";
  return new Intl.NumberFormat(locale || undefined, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

function normalizeText(value) {
  return String(value || "").trim();
}

export default function EstimationSemblables() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || undefined;
  const [artworks, setArtworks] = useState([]);
  const [selectedArtworkId, setSelectedArtworkId] = useState("");
  const [comparables, setComparables] = useState([]);
  const [importedComparables, setImportedComparables] = useState([]);
  const [selectedImportedComparableIds, setSelectedImportedComparableIds] = useState([]);
  const [externalComparables, setExternalComparables] = useState([]);
  const [moyenneEstimee, setMoyenneEstimee] = useState(null);

  const [userRole, setUserRole] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loadingImportedComparables, setLoadingImportedComparables] = useState(false);

  const [isVisionLoading, setIsVisionLoading] = useState(false);
  const [visionError, setVisionError] = useState(null);
  const [visionEstimate, setVisionEstimate] = useState(null);

  const navigate = useNavigate();

  const VISION_API_BASE =
    import.meta.env.VITE_HETZNER_API_URL ||
    import.meta.env.VITE_API_URL ||
    "";

  const selectedArtwork = useMemo(
    () => artworks.find((a) => a.id === selectedArtworkId) || null,
    [artworks, selectedArtworkId]
  );

  const targetClientId = useMemo(() => {
    if (selectedArtwork?.client_id) return selectedArtwork.client_id;
    return currentUserId;
  }, [selectedArtwork?.client_id, currentUserId]);

  const selectedImportedComparables = useMemo(
    () => importedComparables.filter((c) => selectedImportedComparableIds.includes(c.id)),
    [importedComparables, selectedImportedComparableIds]
  );

  const allComparableValues = useMemo(() => {
    return [
      ...comparables.map((c) => toNumber(c.estimated_value)),
      ...selectedImportedComparables.map((c) => toNumber(c.estimated_value || c.hammer_price)),
      ...externalComparables.map((c) => toNumber(c.estimated_value)),
    ].filter((v) => v !== null);
  }, [comparables, selectedImportedComparables, externalComparables]);

  useEffect(() => {
    const fetchArtworksAndRole = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) return;
      setCurrentUserId(session.user.id);

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("role,plan")
        .eq("id", session.user.id)
        .single();

      const role = profileData?.role || "user";
      const plan = profileData?.plan || "standard";
      setUserRole(role);
      setUserPlan(plan);

      if (profileError) {
        console.error("Erreur récupération rôle profile :", profileError);
      }

      let artworksData = [];

      if (role === "expert") {
        const { data: perms } = await supabase
          .from("collection_permissions")
          .select("collection_id")
          .eq("grantee_id", session.user.id);

        const collectionIds = (perms || []).map((p) => p.collection_id).filter(Boolean);

        if (collectionIds.length > 0) {
          const { data } = await supabase
            .from("artworks")
            .select("*")
            .in("collection_id", collectionIds);
          artworksData = data || [];
        }
      } else {
        const { data } = await supabase
          .from("artworks")
          .select("*")
          .eq("client_id", session.user.id);
        artworksData = data || [];
      }

      setArtworks(artworksData);
    };

    fetchArtworksAndRole();
  }, []);

  useEffect(() => {
    const avg = allComparableValues.length >= 3
      ? allComparableValues.reduce((acc, val) => acc + val, 0) / allComparableValues.length
      : null;

    setMoyenneEstimee(avg !== null ? avg.toFixed(2) : null);
    setVisionEstimate(null);
    setVisionError(null);
  }, [allComparableValues]);

  useEffect(() => {
    async function fetchImportedComparables() {
      if (!selectedArtwork || !currentUserId || !targetClientId) {
        setImportedComparables([]);
        setSelectedImportedComparableIds([]);
        return;
      }

      setLoadingImportedComparables(true);

      const visibilityFilters = [
        "visibility.eq.team_artkiv",
        `owner_id.eq.${targetClientId}`,
        `client_id.eq.${targetClientId}`,
        `created_by.eq.${currentUserId}`,
      ];

      if (userRole === "expert") visibilityFilters.push(`expert_id.eq.${currentUserId}`);
      if (isTeamRole(userRole)) visibilityFilters.push("visibility.eq.shared");

      const fields = `
        id,source_type,title,artist,estimated_value,artwork_id,source,medium,dimensions,comment,
        auction_house,auction_house_id,auction_date,price_currency,hammer_price,
        estimate_low,estimate_high,width_cm,height_cm,depth_cm,material,source_file,
        source_page,parsed_at,validated,owner_id,client_id,expert_id,created_by,uploaded_by,
        role_uploaded,visibility
      `;

      async function runQuery(withArtistFilter) {
        let query = supabase
          .from("estimation_comparables")
          .select(fields)
          .eq("source_type", "externe")
          .or(visibilityFilters.join(","))
          .order("auction_date", { ascending: false, nullsFirst: false })
          .limit(60);

        if (withArtistFilter && normalizeText(selectedArtwork.artist).length >= 2) {
          query = query.ilike("artist", `%${normalizeText(selectedArtwork.artist)}%`);
        }

        return query;
      }

      try {
        let { data, error } = await runQuery(true);

        if (!error && (!data || data.length === 0)) {
          const fallback = await runQuery(false);
          data = fallback.data;
          error = fallback.error;
        }

        if (error) {
          console.error("Erreur chargement comparables importés:", error);
          setImportedComparables([]);
        } else {
          const selectedId = selectedArtwork.id;
          setImportedComparables((data || []).filter((c) => c.artwork_id !== selectedId));
        }
      } finally {
        setSelectedImportedComparableIds([]);
        setLoadingImportedComparables(false);
      }
    }

    fetchImportedComparables();
  }, [selectedArtwork, currentUserId, targetClientId, userRole]);

  const handleComparableToggle = (artwork) => {
    const isSelected = comparables.find((a) => a.id === artwork.id);
    if (isSelected) {
      setComparables(comparables.filter((a) => a.id !== artwork.id));
    } else {
      setComparables([...comparables, artwork]);
    }
  };

  const handleImportedComparableToggle = (id) => {
    setSelectedImportedComparableIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const addExternalComparable = () => {
    if (externalComparables.length >= 5) {
      alert(t("round36.estimationSemblables.alerts.maxExternal"));
      return;
    }

    setExternalComparables([
      ...externalComparables,
      {
        title: "",
        artist: "",
        source: "",
        estimated_value: "",
        medium: "",
        dimensions: "",
        comment: "",
      },
    ]);
  };

  const removeExternalComparable = (index) => {
    const updated = [...externalComparables];
    updated.splice(index, 1);
    setExternalComparables(updated);
  };

  const handleExternalComparableChange = (index, field, value) => {
    const updated = [...externalComparables];
    updated[index][field] = value;
    setExternalComparables(updated);
  };

  const getComparableContext = () => {
    const isForExpertClient = userRole === "expert" && targetClientId && targetClientId !== currentUserId;

    return {
      owner_id: targetClientId || currentUserId,
      client_id: targetClientId || null,
      expert_id: userRole === "expert" ? currentUserId : null,
      created_by: currentUserId,
      uploaded_by: currentUserId,
      role_uploaded: userRole || "user",
      visibility: isTeamRole(userRole)
        ? "team_artkiv"
        : isForExpertClient
          ? "expert_client"
          : "private",
    };
  };

  const runVisionAnalysis = async () => {
    if (userRole !== "expert") return;

    if (!selectedArtworkId || comparables.length + selectedImportedComparables.length + externalComparables.length < 3) {
      alert(t("round36.estimationSemblables.alerts.selectForVision"));
      return;
    }

    if (!VISION_API_BASE) {
      setVisionError(t("round36.estimationSemblables.errors.visionNotConfigured"));
      return;
    }

    try {
      setIsVisionLoading(true);
      setVisionError(null);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) throw new Error(t("round36.estimationSemblables.errors.notConnected"));

      const payload = {
        artworkId: selectedArtworkId,
        requestedBy: session.user.id,
        clientId: targetClientId,
        marketType: "physical_art",
        baseEstimate: moyenneEstimee ? parseFloat(moyenneEstimee) : null,
        artwork: selectedArtwork
          ? {
              id: selectedArtwork.id,
              title: selectedArtwork.title,
              artist: selectedArtwork.artist,
              year: selectedArtwork.year,
              medium: selectedArtwork.medium,
              dimensions: selectedArtwork.dimensions,
              estimated_value: selectedArtwork.estimated_value,
              image_url: selectedArtwork.image_url,
            }
          : null,
        comparables: comparables.map((c) => ({
          type: "internal",
          artwork_id: c.id,
          title: c.title,
          artist: c.artist,
          medium: c.medium,
          dimensions: c.dimensions,
          estimated_value: c.estimated_value,
        })),
        importedComparables: selectedImportedComparables.map((c) => ({
          type: "imported_artprice",
          comparable_id: c.id,
          title: c.title,
          artist: c.artist,
          source: c.source,
          auction_house: c.auction_house,
          auction_date: c.auction_date,
          hammer_price: c.hammer_price,
          estimate_low: c.estimate_low,
          estimate_high: c.estimate_high,
          estimated_value: c.estimated_value || c.hammer_price,
          medium: c.medium,
          dimensions: c.dimensions,
          material: c.material,
          comment: c.comment,
        })),
        externalComparables: externalComparables.map((c) => ({
          type: "external_manual",
          title: c.title,
          artist: c.artist,
          source: c.source,
          medium: c.medium,
          dimensions: c.dimensions,
          estimated_value: c.estimated_value,
          comment: c.comment,
        })),
      };

      const response = await fetch(`${VISION_API_BASE}/api/valuations/vision-comparables`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || t("round36.estimationSemblables.errors.vision"));
      }

      const result = await response.json();

      setVisionEstimate({
        estimated_value: result?.estimated_value ?? null,
        confidence_score: result?.confidence_score ?? null,
        range_low: result?.range_low ?? null,
        range_high: result?.range_high ?? null,
        summary: result?.summary ?? null,
      });
    } catch (error) {
      console.error("Erreur analyse Vision :", error);
      setVisionError(error.message || t("round36.estimationSemblables.errors.visionAnalysis"));
      setVisionEstimate(null);
    } finally {
      setIsVisionLoading(false);
    }
  };

  const enregistrerEstimation = async () => {
    if (!selectedArtworkId || comparables.length + selectedImportedComparables.length + externalComparables.length < 3) return;

    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session) {
      alert(t("round36.estimationSemblables.errors.notConnected"));
      return;
    }

    const moyenneFixee = visionEstimate?.estimated_value || moyenneEstimee;
    const context = getComparableContext();

    const generatePdfContent = () => {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(t("round36.estimationSemblables.pdf.title"), 20, 20);

      doc.setFontSize(12);
      doc.text(t("round36.estimationSemblables.pdf.date", { date: new Date().toLocaleDateString(locale) }), 20, 35);
      doc.text(t("round36.estimationSemblables.pdf.artwork", { title: selectedArtwork?.title || t("round36.estimationSemblables.unknownArtwork") }), 20, 45);
      doc.text(t("round36.estimationSemblables.pdf.estimatedValue", { value: formatEuro(moyenneFixee, locale) }), 20, 55);

      if (visionEstimate?.estimated_value) {
        doc.text(t("round36.estimationSemblables.pdf.visionValue", { value: formatEuro(visionEstimate.estimated_value, locale) }), 20, 65);
        if (visionEstimate?.confidence_score !== null && visionEstimate?.confidence_score !== undefined) {
          doc.text(t("round36.estimationSemblables.pdf.visionConfidence", { value: visionEstimate.confidence_score }), 20, 75);
        }
      }

      const internalStartY = visionEstimate?.estimated_value ? 90 : 70;
      doc.text(t("round36.estimationSemblables.pdf.internalComparables"), 20, internalStartY);
      comparables.forEach((c, i) => {
        doc.text(`• ${c.title} (${formatEuro(c.estimated_value, locale)})`, 25, internalStartY + 10 + i * 7);
      });

      const importedStart = internalStartY + 10 + comparables.length * 7 + 10;
      doc.text(t("round36.estimationSemblables.pdf.importedComparables"), 20, importedStart);
      selectedImportedComparables.forEach((c, i) => {
        const value = c.estimated_value || c.hammer_price;
        doc.text(`• ${c.title} (${formatEuro(value, locale)}) – ${c.auction_house || c.source || "—"}`, 25, importedStart + 10 + i * 7);
      });

      const extStart = importedStart + 10 + selectedImportedComparables.length * 7 + 10;
      doc.text(t("round36.estimationSemblables.pdf.externalComparables"), 20, extStart);
      externalComparables.forEach((c, i) => {
        const y = extStart + 10 + i * 7;
        doc.text(`• ${c.title} (${formatEuro(c.estimated_value, locale)}) – ${c.comment || "–"}`, 25, y);
      });

      if (visionEstimate?.summary) {
        const summaryStart = extStart + 20 + externalComparables.length * 7;
        doc.text(t("round36.estimationSemblables.pdf.visionReading"), 20, summaryStart);
        doc.text(String(visionEstimate.summary).slice(0, 180), 25, summaryStart + 10);
      }

      return doc;
    };

    const { data: estimationData, error: insertError } = await supabase
      .from("estimations")
      .insert([
        {
          artwork_id: selectedArtworkId,
          client_id: targetClientId,
          estimation_date: new Date().toISOString(),
          estimated_value: moyenneFixee,
          method: visionEstimate?.estimated_value
            ? "Estimation semblables + Vision"
            : "Estimation semblables",
          notes: visionEstimate?.summary
            ? `Basée sur des œuvres comparables internes, importées et externes. Affinage Vision : ${visionEstimate.summary}`
            : "Basée sur des œuvres comparables internes, importées et externes",
          document_url: null,
        },
      ])
      .select();

    if (insertError) {
      console.error("❌ Erreur insertion estimation :", insertError);
      alert(t("round36.estimationSemblables.alerts.saveError"));
      return;
    }

    const estimationId = estimationData[0].id;

    for (const comp of comparables) {
      await supabase.from("estimation_comparables").insert([
        {
          estimation_id: estimationId,
          source_type: "interne",
          artwork_id: comp.id,
          title: comp.title,
          artist: comp.artist,
          medium: comp.medium,
          dimensions: comp.dimensions,
          estimated_value: toNumber(comp.estimated_value),
          ...context,
        },
      ]);
    }

    for (const comp of selectedImportedComparables) {
      await supabase.from("estimation_comparables").insert([
        {
          estimation_id: estimationId,
          source_type: "externe",
          title: comp.title,
          artist: comp.artist,
          source: comp.source || "artprice_pdf",
          estimated_value: toNumber(comp.estimated_value || comp.hammer_price),
          medium: comp.medium,
          dimensions: comp.dimensions,
          comment: comp.comment,
          auction_house: comp.auction_house,
          auction_house_id: comp.auction_house_id,
          auction_date: comp.auction_date,
          price_currency: comp.price_currency || "EUR",
          hammer_price: toNumber(comp.hammer_price),
          estimate_low: toNumber(comp.estimate_low),
          estimate_high: toNumber(comp.estimate_high),
          width_cm: toNumber(comp.width_cm),
          height_cm: toNumber(comp.height_cm),
          depth_cm: toNumber(comp.depth_cm),
          material: comp.material,
          source_file: comp.source_file,
          source_page: comp.source_page,
          parsed_at: comp.parsed_at,
          validated: comp.validated ?? false,
          ...context,
        },
      ]);
    }

    for (const comp of externalComparables) {
      await supabase.from("estimation_comparables").insert([
        {
          estimation_id: estimationId,
          source_type: "externe",
          title: comp.title,
          artist: comp.artist,
          source: comp.source || "manual",
          estimated_value: toNumber(comp.estimated_value),
          medium: comp.medium,
          dimensions: comp.dimensions,
          comment: comp.comment,
          price_currency: "EUR",
          ...context,
        },
      ]);
    }

    const doc = generatePdfContent();
    const pdfBlob = doc.output("blob");

    const folder = `documents/${selectedArtworkId}`;
    const fileName = `rapport_valuation_${estimationId}.pdf`;
    const fullPath = `${folder}/${fileName}`;

    await supabase.storage.from("documents").remove([fullPath]);

    const { error: uploadError } = await supabase.storage
      .from("documents")
      .upload(fullPath, pdfBlob);

    if (uploadError) {
      console.error("Erreur upload Supabase :", uploadError);
      alert(t("round36.estimationSemblables.alerts.uploadError"));
      return;
    }

    const { data: urlData } = supabase.storage.from("documents").getPublicUrl(fullPath);
    const publicUrl = urlData?.publicUrl;

    await supabase.from("reports").insert([
      {
        title: "Rapport d'estimation",
        file_url: publicUrl,
        type: "valuation",
        client_id: targetClientId,
        artwork_id: selectedArtworkId,
      },
    ]);

    await supabase
      .from("estimations")
      .update({ document_url: publicUrl })
      .eq("id", estimationId);

    const { error: updateError } = await supabase
      .from("artworks")
      .update({
        estimated_value: moyenneFixee,
        last_estimation_date: new Date().toISOString(),
      })
      .eq("id", selectedArtworkId);

    if (updateError) {
      alert(t("round36.estimationSemblables.alerts.artworkUpdateError"));
      console.error(updateError);
    } else {
      alert(t("round36.estimationSemblables.alerts.saved"));
      navigate("/estimation");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="title-artsy text-3xl mb-6">{t("round36.estimationSemblables.title")}</h1>

      <p className="text-gray-700 mb-6 leading-relaxed">
        {t("round36.estimationSemblables.description")}
      </p>

      <div className="detail-container-artsy mb-6">
        <label className="label-artsy">{t("round36.estimationSemblables.fields.artwork")}</label>
        <select
          className="form-control-artsy w-full"
          value={selectedArtworkId}
          onChange={(e) => {
            setSelectedArtworkId(e.target.value);
            setComparables([]);
            setSelectedImportedComparableIds([]);
          }}
        >
          <option value="">{t("round36.estimationSemblables.fields.selectArtwork")}</option>
          {artworks.map((art) => (
            <option key={art.id} value={art.id}>
              {art.title} {art.artist ? `— ${art.artist}` : ""}
            </option>
          ))}
        </select>

        {selectedArtwork && (
          <div className="text-xs text-gray-500 mt-2">
            {t("round36.estimationSemblables.clientContext", { client: targetClientId || "—", role: userRole || "—", plan: userPlan || "—" })}
          </div>
        )}
      </div>

      <div className="detail-container-artsy mb-6">
        <label className="label-artsy">{t("round36.estimationSemblables.sections.internal")}</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {artworks.filter((a) => a.id !== selectedArtworkId).map((art) => (
            <div key={art.id} className="flex items-center">
              <input
                type="checkbox"
                checked={comparables.some((c) => c.id === art.id)}
                onChange={() => handleComparableToggle(art)}
                className="mr-2"
              />
              <span>
                {art.title} – {art.estimated_value ? formatEuro(art.estimated_value, locale) : t("round36.estimationSemblables.noEstimatedValue")}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="detail-container-artsy mb-6">
        <label className="label-artsy">{t("round36.estimationSemblables.sections.imported")}</label>
        <div className="text-xs text-gray-500 mt-1 mb-3">
          {t("round36.estimationSemblables.importedHint")}
        </div>

        {loadingImportedComparables && <div className="text-sm text-gray-500">{t("round36.estimationSemblables.loadingImported")}</div>}

        {!loadingImportedComparables && importedComparables.length === 0 && (
          <div className="text-sm text-gray-500 border rounded p-3 bg-gray-50">
            {t("round36.estimationSemblables.noImported")}
          </div>
        )}

        {!loadingImportedComparables && importedComparables.length > 0 && (
          <div className="space-y-2 mt-3">
            {importedComparables.map((comp) => {
              const value = comp.estimated_value || comp.hammer_price;
              return (
                <label key={comp.id} className="flex gap-3 border rounded p-3 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedImportedComparableIds.includes(comp.id)}
                    onChange={() => handleImportedComparableToggle(comp.id)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {comp.artist || t("round36.estimationSemblables.unknownArtist")} — {comp.title || t("round36.estimationSemblables.untitled")}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {formatEuro(value, locale)} • {comp.auction_house || comp.source || t("round36.estimationSemblables.unknownSource")}
                      {comp.auction_date ? ` • ${new Date(comp.auction_date).toLocaleDateString(locale)}` : ""}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {[comp.medium, comp.material, comp.dimensions].filter(Boolean).join(" • ") || t("round36.estimationSemblables.noDetails")}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                      {t("round36.estimationSemblables.visibilityContext", { visibility: comp.visibility || "—", client: comp.client_id || comp.owner_id || t("round36.estimationSemblables.global") })}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="detail-container-artsy mb-6">
        <label className="label-artsy">
          {t("round36.estimationSemblables.sections.manual")}
        </label>

        {externalComparables.map((comp, index) => (
          <div key={index} className="border p-4 rounded mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" placeholder={t("round36.estimationSemblables.placeholders.title")} value={comp.title} onChange={(e) => handleExternalComparableChange(index, "title", e.target.value)} className="form-control-artsy" />
              <input type="text" placeholder={t("round36.estimationSemblables.placeholders.artist")} value={comp.artist} onChange={(e) => handleExternalComparableChange(index, "artist", e.target.value)} className="form-control-artsy" />
              <input type="text" placeholder={t("round36.estimationSemblables.placeholders.source")} value={comp.source} onChange={(e) => handleExternalComparableChange(index, "source", e.target.value)} className="form-control-artsy" />
              <input type="text" placeholder={t("round36.estimationSemblables.placeholders.medium")} value={comp.medium} onChange={(e) => handleExternalComparableChange(index, "medium", e.target.value)} className="form-control-artsy" />
              <input type="text" placeholder={t("round36.estimationSemblables.placeholders.dimensions")} value={comp.dimensions} onChange={(e) => handleExternalComparableChange(index, "dimensions", e.target.value)} className="form-control-artsy" />
              <input type="number" placeholder={t("round36.estimationSemblables.placeholders.value")} value={comp.estimated_value} onChange={(e) => handleExternalComparableChange(index, "estimated_value", e.target.value)} className="form-control-artsy" />
              <input type="text" placeholder={t("round36.estimationSemblables.placeholders.comment")} value={comp.comment} onChange={(e) => handleExternalComparableChange(index, "comment", e.target.value)} className="form-control-artsy" />
            </div>

            <button type="button" onClick={() => removeExternalComparable(index)} className="btn-artsy btn-secondary-artsy mt-2">
              {t("round36.estimationSemblables.actions.remove")}
            </button>
          </div>
        ))}

        <button type="button" onClick={addExternalComparable} className="btn-artsy btn-primary-artsy mt-4">
          {t("round36.estimationSemblables.actions.addExternal")}
        </button>
      </div>

      <div className="detail-container-artsy mb-6">
        <label className="label-artsy">{t("round36.estimationSemblables.averageLabel")}</label>
        <div className="text-lg font-semibold mt-2">
          {moyenneEstimee
            ? formatEuro(moyenneEstimee, locale)
            : t("round36.estimationSemblables.minimumComparables")}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {t("round36.estimationSemblables.counts", { internal: comparables.length, imported: selectedImportedComparables.length, manual: externalComparables.length })}
        </div>
      </div>

      {userRole === "expert" && (
        <div className="detail-container-artsy mb-6">
          <label className="label-artsy">{t("round36.estimationSemblables.vision.title")}</label>

          <div className="flex flex-wrap items-center gap-3 mt-3">
            <button type="button" onClick={runVisionAnalysis} className="btn-artsy btn-secondary-artsy" disabled={!moyenneEstimee || isVisionLoading}>
              {isVisionLoading ? t("round36.estimationSemblables.vision.loading") : t("round36.estimationSemblables.vision.action")}
            </button>
          </div>

          {visionError && <div className="mt-3 text-sm text-red-600">{visionError}</div>}

          {visionEstimate?.estimated_value && (
            <div className="mt-4 p-4 rounded border bg-gray-50">
              <div className="text-sm text-gray-500 mb-1">{t("round36.estimationSemblables.vision.result")}</div>
              <div className="text-lg font-semibold">{formatEuro(visionEstimate.estimated_value, locale)}</div>

              {(visionEstimate.range_low || visionEstimate.range_high) && (
                <div className="text-sm text-gray-700 mt-1">
                  {t("round36.estimationSemblables.vision.range", { low: visionEstimate.range_low ? formatEuro(visionEstimate.range_low, locale) : "—", high: visionEstimate.range_high ? formatEuro(visionEstimate.range_high, locale) : "—" })}
                </div>
              )}

              {visionEstimate.confidence_score !== null && visionEstimate.confidence_score !== undefined && (
                <div className="text-sm text-gray-700 mt-1">{t("round36.estimationSemblables.vision.confidence", { value: visionEstimate.confidence_score })}</div>
              )}

              {visionEstimate.summary && <div className="text-sm text-gray-600 mt-2 leading-relaxed">{visionEstimate.summary}</div>}

              <div className="text-xs text-gray-500 mt-3">
                {t("round36.estimationSemblables.vision.saveHint")}
              </div>
            </div>
          )}
        </div>
      )}

      <button type="button" onClick={enregistrerEstimation} className="btn-artsy btn-primary-artsy" disabled={!moyenneEstimee}>
        {t("round36.estimationSemblables.actions.save")}
      </button>
    </div>
  );
}
